/**
 * 调整参数面板（M3-06 / DESIGN.md §6.5）。属性面板槽里显示，
 * 活动图层是调整图层时出现对应控件。
 *
 * 交互契约：拖动期间连续回调 onPreview（显示态覆盖，不进历史），
 * 松手回调一次 onCommit（真实命令，恰好一条历史）。
 */
import type { JSX } from 'solid-js';
import { For, Show, createSignal } from 'solid-js';
import type { Adjustment, AdjustmentLayer } from '../../document/types';
import { BasicPanel, basicParamsToRecord } from './BasicPanel';
import styles from './AdjustmentPanel.module.css';

export interface AdjustmentPanelProps {
  readonly layer: AdjustmentLayer | null;
  readonly onPreview: (adjustment: Adjustment) => void;
  readonly onCommit: (adjustment: Adjustment) => void;
}

function numParam(layer: AdjustmentLayer, key: string, fallback: number): number {
  const value = layer.adjustment.params[key];
  return typeof value === 'number' ? value : fallback;
}

/** 滑杆行：input 期间预览，change（松手）提交。 */
function SliderRow(props: {
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly value: number;
  readonly testid: string;
  readonly onPreview: (value: number) => void;
  readonly onCommit: (value: number) => void;
}): JSX.Element {
  return (
    <label class={styles.row}>
      <span class={styles.label}>{props.label}</span>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step ?? 1}
        value={props.value}
        data-testid={props.testid}
        onInput={(event) => {
          props.onPreview(Number(event.currentTarget.value));
        }}
        onChange={(event) => {
          props.onCommit(Number(event.currentTarget.value));
        }}
      />
      <span class={styles.value}>{props.value}</span>
    </label>
  );
}

/** 勾选框行：勾/取消都是一次即时提交（没有"预览中"的中间态）。 */
function CheckboxRow(props: {
  readonly label: string;
  readonly checked: boolean;
  readonly testid: string;
  readonly onCommit: (checked: boolean) => void;
}): JSX.Element {
  return (
    <label class={styles.row}>
      <span class={styles.label}>{props.label}</span>
      <input
        type="checkbox"
        checked={props.checked}
        data-testid={props.testid}
        onChange={(event) => props.onCommit(event.currentTarget.checked)}
      />
    </label>
  );
}

function toHex(r: number, g: number, b: number): string {
  const byte = (v: number): string => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${byte(r)}${byte(g)}${byte(b)}`;
}

function fromHex(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** 颜色行：input 拖色板期间预览，change（松开/关闭色板）提交。 */
function ColorRow(props: {
  readonly label: string;
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly testid: string;
  readonly onPreview: (r: number, g: number, b: number) => void;
  readonly onCommit: (r: number, g: number, b: number) => void;
}): JSX.Element {
  return (
    <label class={styles.row}>
      <span class={styles.label}>{props.label}</span>
      <input
        type="color"
        value={toHex(props.r, props.g, props.b)}
        data-testid={props.testid}
        onInput={(event) => props.onPreview(...fromHex(event.currentTarget.value))}
        onChange={(event) => props.onCommit(...fromHex(event.currentTarget.value))}
      />
    </label>
  );
}

const CURVE_SIZE = 255;

function curvePoints(layer: AdjustmentLayer): number[] {
  const raw = layer.adjustment.params['points'];
  return Array.isArray(raw) ? [...raw] : [0, 0, 255, 255];
}

/** 紧凑曲线编辑器：SVG 里拖点，x 被夹在相邻点之间保持单调。 */
function CurvesEditor(props: {
  readonly layer: AdjustmentLayer;
  readonly onPreview: (adjustment: Adjustment) => void;
  readonly onCommit: (adjustment: Adjustment) => void;
}): JSX.Element {
  // 拖动中的临时点表；null = 显示文档里的参数
  const [dragging, setDragging] = createSignal<number[] | null>(null);
  let svg: SVGSVGElement | undefined = undefined;
  let dragIndex = -1;

  const points = (): number[] => dragging() ?? curvePoints(props.layer);

  const toAdjustment = (pts: number[]): Adjustment => ({
    kind: 'curves',
    params: { points: pts },
  });

  const eventPoint = (event: PointerEvent): { x: number; y: number } => {
    const rect = svg!.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * CURVE_SIZE;
    const y = CURVE_SIZE - ((event.clientY - rect.top) / rect.height) * CURVE_SIZE;
    return {
      x: Math.min(CURVE_SIZE, Math.max(0, Math.round(x))),
      y: Math.min(CURVE_SIZE, Math.max(0, Math.round(y))),
    };
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (dragIndex < 0) return;
    const pts = [...points()];
    const { x, y } = eventPoint(event);
    // 端点只能纵向拖；中间点 x 夹在邻点之间保持单调
    const isFirst = dragIndex === 0;
    const isLast = dragIndex === pts.length / 2 - 1;
    const minX = isFirst ? 0 : pts[(dragIndex - 1) * 2]! + 1;
    const maxX = isLast ? CURVE_SIZE : pts[(dragIndex + 1) * 2]! - 1;
    pts[dragIndex * 2] = isFirst ? 0 : isLast ? CURVE_SIZE : Math.min(maxX, Math.max(minX, x));
    pts[dragIndex * 2 + 1] = y;
    setDragging(pts);
    props.onPreview(toAdjustment(pts));
  };

  const onPointerUp = (): void => {
    if (dragIndex < 0) return;
    dragIndex = -1;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    const pts = dragging();
    setDragging(null);
    if (pts !== null) props.onCommit(toAdjustment(pts));
  };

  // 拖动监听挂 window：移出面板/SVG 也不断手，也绕开事件委托对 SVG 子元素的兼容问题
  const beginDrag = (index: number): void => {
    dragIndex = index;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const polyline = (): string => {
    const pts = points();
    const parts: string[] = [];
    for (let i = 0; i < pts.length; i += 2) {
      parts.push(`${pts[i]},${CURVE_SIZE - pts[i + 1]!}`);
    }
    return parts.join(' ');
  };

  return (
    <svg
      ref={(el) => {
        svg = el;
      }}
      class={styles.curves}
      viewBox={`0 0 ${CURVE_SIZE} ${CURVE_SIZE}`}
      data-testid="adjustment-curves"
    >
      <line class={styles.curveGuide} x1="0" y1={CURVE_SIZE} x2={CURVE_SIZE} y2="0" />
      <polyline class={styles.curveLine} points={polyline()} />
      <For each={Array.from({ length: points().length / 2 }, (_, i) => i)}>
        {(index) => (
          <circle
            class={styles.curvePoint}
            cx={points()[index * 2]}
            cy={CURVE_SIZE - points()[index * 2 + 1]!}
            r="8"
            data-testid={`curve-point-${index}`}
            on:pointerdown={(event) => {
              event.preventDefault();
              beginDrag(index);
            }}
          />
        )}
      </For>
    </svg>
  );
}

export function AdjustmentPanel(props: AdjustmentPanelProps): JSX.Element {
  const preview = (key: string, value: number): void => {
    const layer = props.layer!;
    props.onPreview({
      kind: layer.adjustment.kind,
      params: { ...layer.adjustment.params, [key]: value },
    });
  };
  const commit = (key: string, value: number): void => {
    const layer = props.layer!;
    props.onCommit({
      kind: layer.adjustment.kind,
      params: { ...layer.adjustment.params, [key]: value },
    });
  };
  const previewMany = (patch: Record<string, number | readonly number[]>): void => {
    const layer = props.layer!;
    props.onPreview({ kind: layer.adjustment.kind, params: { ...layer.adjustment.params, ...patch } });
  };
  const commitMany = (patch: Record<string, number | readonly number[]>): void => {
    const layer = props.layer!;
    props.onCommit({ kind: layer.adjustment.kind, params: { ...layer.adjustment.params, ...patch } });
  };
  const arrayParam = (layer: AdjustmentLayer, key: string, fallback: readonly number[]): number[] => {
    const value = layer.adjustment.params[key];
    return Array.isArray(value) ? [...value] : [...fallback];
  };

  return (
    <Show when={props.layer} fallback={<p class={styles.empty}>未选择调整图层</p>}>
      {(layer) => (
        <div class={styles.panel} data-testid="adjustment-panel">
          <Show when={layer().adjustment.kind === 'curves'}>
            <CurvesEditor layer={layer()} onPreview={props.onPreview} onCommit={props.onCommit} />
          </Show>
          <Show when={layer().adjustment.kind === 'levels'}>
            <SliderRow label="输入黑场" min={0} max={253} value={numParam(layer(), 'inBlack', 0)}
              testid="levels-inBlack"
              onPreview={(v) => preview('inBlack', v)} onCommit={(v) => commit('inBlack', v)} />
            <SliderRow label="灰度系数" min={0.1} max={3} step={0.01} value={numParam(layer(), 'gamma', 1)}
              testid="levels-gamma"
              onPreview={(v) => preview('gamma', v)} onCommit={(v) => commit('gamma', v)} />
            <SliderRow label="输入白场" min={2} max={255} value={numParam(layer(), 'inWhite', 255)}
              testid="levels-inWhite"
              onPreview={(v) => preview('inWhite', v)} onCommit={(v) => commit('inWhite', v)} />
            <SliderRow label="输出黑场" min={0} max={255} value={numParam(layer(), 'outBlack', 0)}
              testid="levels-outBlack"
              onPreview={(v) => preview('outBlack', v)} onCommit={(v) => commit('outBlack', v)} />
            <SliderRow label="输出白场" min={0} max={255} value={numParam(layer(), 'outWhite', 255)}
              testid="levels-outWhite"
              onPreview={(v) => preview('outWhite', v)} onCommit={(v) => commit('outWhite', v)} />
          </Show>
          <Show when={layer().adjustment.kind === 'hsl'}>
            <SliderRow label="色相" min={-180} max={180} value={numParam(layer(), 'hue', 0)}
              testid="hsl-hue"
              onPreview={(v) => preview('hue', v)} onCommit={(v) => commit('hue', v)} />
            <SliderRow label="饱和度" min={-100} max={100} value={numParam(layer(), 'saturation', 0)}
              testid="hsl-saturation"
              onPreview={(v) => preview('saturation', v)} onCommit={(v) => commit('saturation', v)} />
            <SliderRow label="明度" min={-100} max={100} value={numParam(layer(), 'lightness', 0)}
              testid="hsl-lightness"
              onPreview={(v) => preview('lightness', v)} onCommit={(v) => commit('lightness', v)} />
          </Show>
          <Show when={layer().adjustment.kind === 'lut'}>
            {/* 3D LUT 是烘好的查找表，不是可拖的参数——面板只展示已应用哪张表 */}
            <p class={styles.lutInfo} data-testid="lut-info">
              已应用 {numParam(layer(), 'size', 0)}³ 颜色查找表
            </p>
          </Show>
          <Show when={layer().adjustment.kind === 'basic'}>
            <BasicPanel
              params={layer().adjustment.params}
              onPreview={(params) => props.onPreview({ kind: 'basic', params: basicParamsToRecord(params) })}
              onCommit={(params) => props.onCommit({ kind: 'basic', params: basicParamsToRecord(params) })}
            />
          </Show>
          <Show when={layer().adjustment.kind === 'blackAndWhite'}>
            <SliderRow label="红" min={0} max={300} value={numParam(layer(), 'r', 40)}
              testid="bw-r" onPreview={(v) => preview('r', v)} onCommit={(v) => commit('r', v)} />
            <SliderRow label="绿" min={0} max={300} value={numParam(layer(), 'g', 40)}
              testid="bw-g" onPreview={(v) => preview('g', v)} onCommit={(v) => commit('g', v)} />
            <SliderRow label="蓝" min={0} max={300} value={numParam(layer(), 'b', 20)}
              testid="bw-b" onPreview={(v) => preview('b', v)} onCommit={(v) => commit('b', v)} />
          </Show>
          <Show when={layer().adjustment.kind === 'photoFilter'}>
            <ColorRow label="滤镜颜色"
              r={numParam(layer(), 'r', 255)} g={numParam(layer(), 'g', 157)} b={numParam(layer(), 'b', 0)}
              testid="photofilter-color"
              onPreview={(r, g, b) => previewMany({ r, g, b })}
              onCommit={(r, g, b) => commitMany({ r, g, b })} />
            <SliderRow label="浓度" min={0} max={100} value={numParam(layer(), 'density', 0)}
              testid="photofilter-density"
              onPreview={(v) => preview('density', v)} onCommit={(v) => commit('density', v)} />
            <CheckboxRow label="保留明度" checked={numParam(layer(), 'preserveLuminosity', 1) !== 0}
              testid="photofilter-preserve"
              onCommit={(checked) => commit('preserveLuminosity', checked ? 1 : 0)} />
          </Show>
          <Show when={layer().adjustment.kind === 'channelMixer'}>
            <For each={['r', 'g', 'b'] as const}>
              {(out) => (
                <>
                  <p class={styles.lutInfo}>输出：{out === 'r' ? '红' : out === 'g' ? '绿' : '蓝'}</p>
                  <For each={['r', 'g', 'b', 'c'] as const}>
                    {(inp) => {
                      const key = `${out}${inp}`;
                      const label = inp === 'c' ? '常量' : inp === 'r' ? '红' : inp === 'g' ? '绿' : '蓝';
                      const fallback = inp === out ? 100 : 0;
                      return (
                        <SliderRow label={label} min={-200} max={200}
                          value={numParam(layer(), key, fallback)} testid={`channelmixer-${key}`}
                          onPreview={(v) => preview(key, v)} onCommit={(v) => commit(key, v)} />
                      );
                    }}
                  </For>
                </>
              )}
            </For>
          </Show>
          <Show when={layer().adjustment.kind === 'gradientMap'}>
            <ColorRow label="阴影颜色"
              r={arrayParam(layer(), 'color1', [0, 0, 0])[0]!}
              g={arrayParam(layer(), 'color1', [0, 0, 0])[1]!}
              b={arrayParam(layer(), 'color1', [0, 0, 0])[2]!}
              testid="gradientmap-color1"
              onPreview={(r, g, b) => previewMany({ color1: [r, g, b] })}
              onCommit={(r, g, b) => commitMany({ color1: [r, g, b] })} />
            <ColorRow label="高光颜色"
              r={arrayParam(layer(), 'color2', [255, 255, 255])[0]!}
              g={arrayParam(layer(), 'color2', [255, 255, 255])[1]!}
              b={arrayParam(layer(), 'color2', [255, 255, 255])[2]!}
              testid="gradientmap-color2"
              onPreview={(r, g, b) => previewMany({ color2: [r, g, b] })}
              onCommit={(r, g, b) => commitMany({ color2: [r, g, b] })} />
          </Show>
          <Show when={layer().adjustment.kind === 'posterize'}>
            <SliderRow label="色阶数" min={2} max={256} value={numParam(layer(), 'levels', 256)}
              testid="posterize-levels"
              onPreview={(v) => preview('levels', v)} onCommit={(v) => commit('levels', v)} />
          </Show>
          <Show when={layer().adjustment.kind === 'threshold'}>
            <SliderRow label="阈值" min={1} max={255} value={numParam(layer(), 'level', 128)}
              testid="threshold-level"
              onPreview={(v) => preview('level', v)} onCommit={(v) => commit('level', v)} />
          </Show>
          <Show when={layer().adjustment.kind === 'vibrance'}>
            <SliderRow label="自然饱和度" min={-100} max={100} value={numParam(layer(), 'amount', 0)}
              testid="vibrance-amount"
              onPreview={(v) => preview('amount', v)} onCommit={(v) => commit('amount', v)} />
          </Show>
          <Show when={layer().adjustment.kind === 'colorBalance'}>
            <For each={['shadow', 'mid', 'high'] as const}>
              {(range) => (
                <>
                  <p class={styles.lutInfo}>
                    {range === 'shadow' ? '阴影' : range === 'mid' ? '中间调' : '高光'}
                  </p>
                  <For each={['R', 'G', 'B'] as const}>
                    {(channel) => {
                      const key = `${range}${channel}`;
                      return (
                        <SliderRow label={channel} min={-100} max={100}
                          value={numParam(layer(), key, 0)} testid={`colorbalance-${key}`}
                          onPreview={(v) => preview(key, v)} onCommit={(v) => commit(key, v)} />
                      );
                    }}
                  </For>
                </>
              )}
            </For>
            <CheckboxRow label="保留明度" checked={numParam(layer(), 'preserveLuminosity', 1) !== 0}
              testid="colorbalance-preserve"
              onCommit={(checked) => commit('preserveLuminosity', checked ? 1 : 0)} />
          </Show>
          <Show when={layer().adjustment.kind === 'brightnessContrast'}>
            <SliderRow label="亮度" min={-100} max={100} value={numParam(layer(), 'brightness', 0)}
              testid="brightnesscontrast-brightness"
              onPreview={(v) => preview('brightness', v)} onCommit={(v) => commit('brightness', v)} />
            <SliderRow label="对比度" min={-100} max={100} value={numParam(layer(), 'contrast', 0)}
              testid="brightnesscontrast-contrast"
              onPreview={(v) => preview('contrast', v)} onCommit={(v) => commit('contrast', v)} />
          </Show>
        </div>
      )}
    </Show>
  );
}
